import { logger } from '@lib/pp-shop-lib/helpers'

export function consumeSsoCallback(browser = window) {
    const url = new URL(browser.location.href)
    if (!url.searchParams.has('sso_token') && !url.searchParams.has('sso_error')) return null

    const callback = {
        token: url.searchParams.get('sso_token'),
        error: url.searchParams.get('sso_error')
    }
    url.searchParams.delete('sso_token')
    url.searchParams.delete('sso_error')
    browser.history.replaceState(browser.history.state, '', url.href)
    return callback
}

export async function trySsoHandover(config, auth, callback, browser = window) {
    const url = new URL(browser.location.href)
    // Native apps and local development must not enter the browser redirect flow.
    if (!config.legacySsoUrl || url.protocol !== 'https:' ||
        !['appdev.kalender-druck.de', 'app.kalender-druck.de'].includes(url.hostname)) {
        return 'skipped'
    }
    if (url.pathname.split('/').pop() === 'logout') return 'skipped'

    if (callback) {
        auth.ssoAutoLoginBlocked = true
        if (callback.error || !/^[a-f0-9]{64}$/i.test(callback.token || '')) {
            logger.warn('[sso] Legacy login missing or invalid SSO callback')
            return 'failed'
        }
        try {
            await auth.exchangeSsoToken(callback.token)
            return 'authenticated'
        } catch {
            // Do not send the callback token or API response to telemetry.
            auth.ssoAutoLoginBlocked = true
            logger.warn('[sso] Token exchange failed; manual login remains available')
            return 'failed'
        }
    }

    const hasValidLogin = () => Boolean(auth.user?.token && auth.user?.kid &&
        new Date(auth.user?.access?.expires).getTime() > Date.now())
    if (hasValidLogin()) return 'skipped'

    if (auth.user?.refresh_token) {
        try {
            await auth.refreshLogin()
            if (hasValidLogin()) return 'authenticated'
        } catch (error) {
            logger.warn('[sso] Existing login could not be refreshed')
            await auth.logout(true)
            if (![401, 403].includes(error.status)) return 'failed'
        }
    }
    if (auth.user?.token || auth.user?.kid) await auth.logout(true)

    const hasSignal = browser.document.cookie.split(';')
        .some(cookie => cookie.trim() === 'user_sso=1')
    if (!hasSignal) {
        // Without a legacy session there is no loop risk; a later legacy login may hand over again.
        auth.ssoAutoLoginBlocked = false
        return 'skipped'
    }
    if (auth.ssoAutoLoginBlocked) return 'skipped'

    let target
    try {
        target = new URL(config.legacySsoUrl)
    } catch {
        logger.warn('[sso] Invalid legacy SSO URL configuration')
        return 'failed'
    }
    if (target.protocol !== 'https:' || target.username || target.password ||
        target.port || !['demo.kalender-druck.de', 'www.kalender-druck.de'].includes(target.hostname)) {
        logger.warn('[sso] Invalid legacy SSO URL configuration')
        return 'failed'
    }
    target.searchParams.set('redirect_uri', url.href)
    // Persist before leaving: even a failed callback must not create a redirect loop.
    auth.ssoAutoLoginBlocked = true
    auth.$persist?.()
    browser.location.replace(target.href)
    return 'redirecting'
}
